/* eslint-disable react/jsx-props-no-spreading */
import classNames from 'classnames';
// import isAfter from 'date-fns/isAfter';
import { isBefore } from 'date-fns/isBefore';
import { parseISO } from 'date-fns/parseISO';
import { useCallback, useMemo, useState } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';

import { useThemeClassName } from '../../../contexts/ThemeContext';
import Button from '../../buttons/Button';
import ExplanationModal from '../../modals/ExplanationModal';

import styles from '../../../../styles/items/games/default.module.scss';

interface GroupGameItemProps {
    game: Game;
    className?: string;
    cardClassName?: string;
}

function GroupGameItem({ game, className = null, cardClassName = null }: GroupGameItemProps) {
    const themeClassName = useThemeClassName(styles);
    const { games = null } = game || {};
    const [firstGame = null] = games || [];
    const { title = null } = firstGame || {};

    const [modalOpen, setModalOpen] = useState(false);
    const [modalData, setModalData] = useState(null);

    const openModal = useCallback(
        (data) => {
            setModalData(data);
            setModalOpen(true);
        },
        [setModalOpen, setModalData],
    );

    const closeModal = useCallback(() => {
        setModalOpen(false);
    }, [setModalOpen]);

    return (
        <div className={classNames([styles.container, themeClassName, className])}>
            {title !== null ? <h2 className={styles.title}>{title}</h2> : null}
            <div className={styles.inner}>
                {games.map((gameItem) => {
                    const {
                        id = null,
                        url = null,
                        title = null,
                        startAt = null,
                        endAt = null,
                        canParticipateAt = null,
                        explanationModal = null,
                    } = gameItem;

                    const startAtDate = useMemo(
                        () => (startAt !== null ? parseISO(startAt) : null),
                        [startAt],
                    );
                    const endAtDate = useMemo(
                        () => (endAt !== null ? parseISO(endAt) : null),
                        [endAt],
                    );
                    const canParticipateAtDate = useMemo(
                        () => (canParticipateAt !== null ? parseISO(canParticipateAt) : null),
                        [canParticipateAt],
                    );

                    const isStarted = useMemo(
                        () => startAtDate === null || isBefore(startAtDate, new Date()),
                        [startAtDate],
                    );
                    const isEnded = useMemo(
                        () => endAtDate !== null && isBefore(endAtDate, new Date()),
                        [endAtDate],
                    );

                    return (
                        <Button
                            key={`game-${id}`}
                            href={isStarted && !isEnded && explanationModal === null ? url : null}
                            onClick={
                                !modalOpen && explanationModal !== null
                                    ? () => openModal({ url, blocks: explanationModal })
                                    : null
                            }
                            disabled={!isStarted || isEnded}
                            className={classNames([
                                styles.card,
                                {
                                    [cardClassName]: cardClassName !== null,
                                },
                            ])}
                        >
                            <span className={styles.button}>
                                {isEnded ? (
                                    <FormattedMessage
                                        defaultMessage="Ended on {date}"
                                        values={{
                                            date: (
                                                <FormattedDate
                                                    value={endAtDate}
                                                    day="numeric"
                                                    month="short"
                                                />
                                            ),
                                        }}
                                        description="Button label"
                                    />
                                ) : null}

                                {!isStarted ? (
                                    <FormattedMessage
                                        defaultMessage="Start on {date}"
                                        values={{
                                            date: (
                                                <FormattedDate
                                                    value={startAtDate}
                                                    day="numeric"
                                                    month="short"
                                                />
                                            ),
                                        }}
                                        description="Button label"
                                    />
                                ) : null}

                                {isStarted && !isEnded && canParticipateAtDate === null ? (
                                    title !== null ? (
                                        <h3 className={styles.buttonTitle}>{title}</h3>
                                    ) : (
                                        <FormattedMessage
                                            defaultMessage="Play"
                                            description="Button label"
                                        />
                                    )
                                ) : null}

                                {isStarted && !isEnded && canParticipateAtDate !== null ? (
                                    <FormattedMessage
                                        defaultMessage="Play on {date}"
                                        values={{
                                            date: (
                                                <FormattedDate
                                                    value={canParticipateAtDate}
                                                    day="numeric"
                                                    month="short"
                                                />
                                            ),
                                        }}
                                        description="Button label"
                                    />
                                ) : null}
                            </span>
                        </Button>
                    );
                })}
            </div>
            {modalOpen ? <ExplanationModal {...modalData} onClose={closeModal} /> : null}
        </div>
    );
}

export default GroupGameItem;
